import { fixed, trim } from '../../math/format';
import { gearRatio, proportion } from '../../math/material';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

const TOOTH_COUNTS = [20, 24, 28, 30, 32, 36, 40, 44, 48, 56, 60, 64, 72, 80] as const;

export const RATIO_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'ratio.gears',
    topicId: 'ratio',
    skill: 'Reducing a gear ratio',
    gen: (rng) => {
      const driving = rng.pick(TOOTH_COUNTS);
      const driven = rng.pick(TOOTH_COUNTS.filter((t) => t !== driving));
      const solved = gearRatio(driving, driven);
      const { a, b } = solved.value;

      return {
        kind: 'mc',
        prompt: `A ${driving}-tooth gear driving a ${driven}-tooth gear. What is the ratio?`,
        formula: 'ratio = driving teeth : driven teeth',
        why: `${driving} : ${driven} reduces to ${a} : ${b}. ${
          solved.value.decimal < 1
            ? 'The driven gear is bigger, so the output turns slower with more torque.'
            : 'The driving gear is bigger, so the output turns faster.'
        }`,
        steps: solved.steps,
        ...mcOptions(rng, `${a} : ${b}`, [
          {
            label: `${b} : ${a}`,
            value: b / a,
            trap: 'Backwards. Driving gear first, driven gear second — swapping them inverts the whole answer.',
          },
          {
            label: `${driving} : ${driven}`,
            value: driving / driven,
            trap: `Right relationship, but not reduced. Both numbers still divide down to ${a} : ${b}.`,
          },
          {
            label: `${a + b} : ${b}`,
            value: (a + b) / b,
            trap: 'You added the two counts. A ratio is a division, not a sum.',
          },
        ]),
      };
    },
  },
  {
    id: 'ratio.outputSpeed',
    topicId: 'ratio',
    skill: 'Output speed through a gear pair',
    gen: (rng) => {
      const driving = rng.pick(TOOTH_COUNTS);
      const driven = rng.pick(TOOTH_COUNTS.filter((t) => t !== driving));
      const inputRpm = rng.step(200, 2000, 100);
      const outputRpm = (inputRpm * driving) / driven;

      return {
        kind: 'mc',
        prompt: `${inputRpm} RPM into a ${driving}-tooth gear, driving a ${driven}-tooth. What comes out?`,
        formula: 'output RPM = input × (driving teeth ÷ driven teeth)',
        why: `${inputRpm} × ${driving} ÷ ${driven} = ${fixed(outputRpm, 0)} RPM. ${
          driven > driving ? 'A bigger driven gear always turns slower.' : 'A smaller driven gear always turns faster.'
        }`,
        steps: [
          { kind: 'formula', text: 'output RPM = input × (driving teeth ÷ driven teeth)' },
          { kind: 'substitute', text: `${inputRpm} × (${driving} ÷ ${driven})` },
          { kind: 'compute', text: `${driving} ÷ ${driven} = ${fixed(driving / driven, 4)}` },
          { kind: 'result', text: `= ${fixed(outputRpm, 0)} RPM` },
          {
            kind: 'note',
            text: 'Teeth and speed run opposite ways: more teeth on the driven gear means fewer turns out of it.',
          },
        ],
        ...mcOptions(rng, `${fixed(outputRpm, 0)} RPM`, [
          {
            label: `${fixed((inputRpm * driven) / driving, 0)} RPM`,
            value: (inputRpm * driven) / driving,
            trap: 'Ratio is upside down. Driven teeth go on the bottom.',
          },
          {
            label: `${inputRpm} RPM`,
            value: inputRpm,
            trap: 'That is the input speed. Gears of different sizes always change it.',
          },
          {
            label: `${fixed(inputRpm * (driven / driving) * (driven / driving), 0)} RPM`,
            value: inputRpm * (driven / driving) ** 2,
            trap: 'You applied the ratio twice. One gear pair, one ratio.',
          },
        ]),
      };
    },
  },
  {
    id: 'ratio.proportion',
    topicId: 'ratio',
    skill: 'Scaling a job by proportion',
    gen: (rng) => {
      const parts = rng.pick([10, 20, 25, 50]);
      const minutes = rng.pick([15, 30, 45, 60, 90]);
      const target = parts * rng.pick([2, 3, 4, 5]);
      const solved = proportion(parts, minutes, target);

      return {
        kind: 'mc',
        prompt: `${parts} parts take ${minutes} minutes. How long for ${target}?`,
        formula: 'cross-multiply: (b × c) ÷ a',
        why: `${parts} is to ${minutes} as ${target} is to what. (${minutes} × ${target}) ÷ ${parts} = ${fixed(solved.value, 0)} minutes.`,
        steps: solved.steps,
        ...mcOptions(rng, `${fixed(solved.value, 0)} minutes`, [
          {
            label: `${fixed((parts * minutes) / target, 0)} minutes`,
            value: (parts * minutes) / target,
            trap: 'Upside down. More parts has to mean more time, not less.',
          },
          {
            label: `${fixed(minutes + (target - parts), 0)} minutes`,
            value: minutes + (target - parts),
            trap: 'You added the difference. Time scales in proportion to the count, it does not step up one for one.',
          },
          {
            label: `${minutes} minutes`,
            value: minutes,
            trap: 'That is the time for the original batch.',
          },
        ]),
      };
    },
  },
  {
    id: 'ratio.scaleDrawing',
    topicId: 'ratio',
    skill: 'Reading a scaled drawing',
    gen: (rng) => {
      const scale = rng.pick([2, 4, 5, 10]);
      const half = rng.pick([true, false]);
      const drawn = rng.step(0.5, 6, 0.25);
      // Half-scale means the drawing is smaller than the part.
      const actual = half ? drawn * scale : drawn / scale;
      const label = half ? `1:${scale}` : `${scale}:1`;

      return {
        kind: 'mc',
        prompt: `A drawing is at ${label} scale. A feature measures ${fixed(drawn, 3)}" on the paper. How big is it really?`,
        sub: half
          ? 'The part is bigger than the drawing.'
          : 'The drawing is blown up bigger than the part.',
        formula: half ? 'actual = drawn × scale' : 'actual = drawn ÷ scale',
        why: `${label} means ${half ? `every inch on paper is ${scale}" on the part` : `every ${scale}" on paper is one inch on the part`}, so ${fixed(drawn, 3)} ${half ? '×' : '÷'} ${scale} = ${fixed(actual, 4)}".`,
        steps: [
          {
            kind: 'note',
            text: `${label} reads as "drawing : part". ${half ? 'The part is the bigger of the two here.' : 'The drawing is the bigger of the two here.'}`,
          },
          { kind: 'formula', text: half ? 'actual = drawn × scale' : 'actual = drawn ÷ scale' },
          { kind: 'substitute', text: `${trim(drawn, 3)} ${half ? '×' : '÷'} ${scale}` },
          { kind: 'result', text: `= ${fixed(actual, 4)}"` },
          {
            kind: 'note',
            text: 'Never scale a drawing with a rule to get a dimension. Use the written number — this is only for sanity-checking one.',
          },
        ],
        ...mcOptions(rng, `${fixed(actual, 4)}"`, [
          {
            label: `${fixed(half ? drawn / scale : drawn * scale, 4)}"`,
            value: half ? drawn / scale : drawn * scale,
            trap: `Wrong direction. At ${label} the part is ${half ? 'bigger' : 'smaller'} than what is drawn.`,
          },
          {
            label: `${fixed(drawn, 4)}"`,
            value: drawn,
            trap: 'That is the size on the paper. A scaled drawing is not full size.',
          },
          {
            label: `${fixed(actual * 2, 4)}"`,
            value: actual * 2,
            trap: 'Scaled twice, or the scale factor was doubled somewhere.',
          },
        ]),
      };
    },
  },
];
